import { URLS } from '@components/common/constants';
import { stores } from '@stores/index';
import theme from '@css/theme';

declare global {
  interface Window {
    zE: {
      show: () => void;
      hide: () => void;
      identify: (e: unknown) => void;
      logout: () => void;
    };
    zESettings: unknown;
  }
}

const openPricingLink = () => {
  window.open(URLS.PRICING, '_blank');
};

class Zendesk {
  private src =
    'https://static.zdassets.com/ekr/snippet.js?key=0b2e42b6-29a0-450a-b0c6-d4fd2eda32df';

  private id = 'ze-snippet';

  private isZendesk = () => Boolean(document.getElementById(this.id) && window.zE);

  private resetSettings = () => {
    window.zESettings = {};
  };

  private getUser = () => {
    const { userStore } = stores;
    return {
      email: userStore.email,
      id: userStore.id,
      name: userStore.name,
    };
  };

  public inject = () => {
    const v = this.isZendesk();
    if (!v) {
      this.resetSettings();
      // https://developer.zendesk.com/documentation/classic-sdks/web-widget/quickstart-tutorials/web-widget-javascript-apis/#using-api-settings-to-customize-the-web-widget
      const script = document.createElement('script');
      script.src = this.src;
      script.async = true;
      script.id = this.id;
      document.head.appendChild(script);
      this.identify();
      this.prefill();
    }
  };

  private dispose = () => {
    const v = this.isZendesk();
    if (v && Object.prototype.hasOwnProperty.call(window.zE, 'logout')) {
      const element = document.getElementById(this.id);
      if (element && Boolean(element.getAttribute('src'))) {
        element.parentNode!.removeChild(element);
        window.zE.logout();
      }
    }
  };

  public enable = () => {
    const v = this.isZendesk();
    if (v && Object.prototype.hasOwnProperty.call(window.zE, 'show')) {
      window.zE.show();
    } else {
      zendesk.inject();
    }
  };

  public disable = () => {
    const v = this.isZendesk();
    if (v && Object.prototype.hasOwnProperty.call(window.zE, 'hide')) {
      window.zE.hide();
    }
  };

  private identify = () => {
    const user = this.getUser();
    const v = this.isZendesk();
    // Move this to window.rudderanalytics.identify - https://docs.rudderstack.com/destinations/crm/zendesk
    if (v && Object.prototype.hasOwnProperty.call(window.zE, 'identify')) {
      window.zE.identify(user);
    }
  };

  private prefill = () => {
    this.resetSettings();
    const user = this.getUser();
    // https://developer.zendesk.com/api-reference/widget/settings/#usage-4
    window.zESettings = {
      webWidget: {
        color: { theme: theme.color.neutral1 },
        contactForm: {
          fields: [{ id: 'email', prefill: { '*': `${user.email}` } }],
        },
      },
    };
  };

  public delete = () => {
    this.disable();
    this.resetSettings();
    this.dispose();
  };
}

const zendesk = new Zendesk();

export { openPricingLink, zendesk };
